import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import type { SubmitEvent } from 'react';

import { useTranslation, type TFunction } from '@mint-lib/i18n';
import { Add, Delete } from '@mint-lib/icons';
import { Button } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import produce from 'immer';

import type { GlossaryAlias, GlossaryItem } from '../../api/types.js';
import { getGlossaryAliasesErrorMessage } from '../../constants/errorsMapping.js';
import { useBulkGlossaryAliasesUpdate } from '../../queries/index.js';
import { Input } from '../atoms/Input.jsx';
import { Tooltip } from '../atoms/Tooltip.jsx';

type GlossaryAliasesFormProps = Readonly<{
  glossary: GlossaryItem;
  codesPerGlossary: number;
  onClose: () => void;
  autoFocusIndex: null | number;
}>;

type BulkAliasPayloadItem = {
  cid: string;
  id?: string;
  name: string;
  action: 'create' | 'update' | 'delete';
};

type BulkAliasResponseItem = {
  error?: Parameters<typeof getGlossaryAliasesErrorMessage>[1];
  data?: { id: string } | null;
};

const hasActiveAliases = (aliases: ExtendedGlossaryAlias[]) =>
  aliases.some((alias) => !alias.deleted);

const deleteAliasAt = (aliases: ExtendedGlossaryAlias[], index: number) =>
  produce(aliases, (draft) => {
    draft[index].deleted = true;

    if (!hasActiveAliases(draft)) {
      draft.push(getInitialAliasValue());
    }
  });

const applyBulkAliasResponse = ({
  alias,
  response,
  t,
}: {
  alias: ExtendedGlossaryAlias;
  response: BulkAliasResponseItem;
  t: TFunction;
}) => {
  if (response.error) {
    alias.deleted = false;
    alias.error = getGlossaryAliasesErrorMessage(t, response.error);
    return;
  }

  if (response.data) {
    alias.id = response.data.id;
  }
};

const getNextAliasesState = ({
  aliases,
  bulkPayload,
  bulkResponse,
  t,
}: {
  aliases: ExtendedGlossaryAlias[];
  bulkPayload: BulkAliasPayloadItem[];
  bulkResponse: BulkAliasResponseItem[];
  t: TFunction;
}) =>
  produce(aliases, (draftAliases) => {
    bulkResponse.forEach((response, index) => {
      const payloadItem = bulkPayload[index];
      const alias = draftAliases.find(({ cid }) => cid === payloadItem.cid);

      if (alias) {
        applyBulkAliasResponse({ alias, response, t });
      }
    });
  });

export const GlossaryAliasesForm = ({
  glossary,
  codesPerGlossary,
  onClose,
  autoFocusIndex,
}: GlossaryAliasesFormProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const initialValue = useMemo(
    () =>
      glossary.codes.length
        ? glossary.codes.map((code) => ({
            ...code,
            cid: globalThis.crypto.randomUUID(),
          }))
        : [getInitialAliasValue()],
    [glossary.codes],
  );

  const [aliases, setAliases] = useState<ExtendedGlossaryAlias[]>(initialValue);
  const currentAliases = aliases.filter((alias) => !alias.deleted);
  const isFormPristine = aliases === initialValue;
  const isFormInvalid = aliases.some((alias) => !alias.deleted && alias.error);

  const autoFocusFieldIndexRef = useRef(autoFocusIndex);

  const handleAddAlias = () => {
    setAliases((prevState) => [...prevState, getInitialAliasValue()]);
    autoFocusFieldIndexRef.current = aliases.length;
  };

  const handleChangeAlias =
    (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      const updatedAlises = produce(aliases, (draftAliases) => {
        draftAliases[index].name = value;
        draftAliases[index].error = null;
      });

      setAliases(updatedAlises);
    };

  const handleDeleteAlias = (index: number) => {
    setAliases(deleteAliasAt(aliases, index));
  };

  const validateForm = () => {
    let validationSuccess = true;

    const updatedAliases = produce(aliases, (draft) => {
      for (const alias of draft) {
        if (!alias.deleted && !alias.name && aliases.length > 1) {
          alias.error = t('The alias name is required');
          validationSuccess = false;
        }
      }
    });

    setAliases(updatedAliases);

    if (!validationSuccess) {
      throw new Error('The alias name is required');
    }
  };

  const { mutateAsync: bulkGlossaryAliasesUpdate, isPending: isSubmitting } =
    useBulkGlossaryAliasesUpdate();

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      validateForm();

      const aliasesToCreate = aliases.filter(
        (alias) => !alias.id && alias.name && !alias.deleted,
      );
      const aliasesToDelete = aliases.filter(
        (alias) => alias.id && alias.deleted,
      );
      const aliasesToUpdate = aliases.filter((alias, index) => {
        const initialAlias = initialValue[index];
        const hasChanged = alias.name !== initialAlias?.name;

        return alias.id && !alias.deleted && hasChanged;
      });

      const createAliasesPayload = aliasesToCreate.map((aliasToCreate) => ({
        name: aliasToCreate.name,
        action: 'create' as const,
        cid: aliasToCreate.cid,
      }));

      const updateAliasesPayload = aliasesToUpdate.map((aliasToUpdate) => ({
        id: aliasToUpdate.id,
        name: aliasToUpdate.name,
        action: 'update' as const,
        cid: aliasToUpdate.cid,
      }));

      const deleteAliasesPayload = aliasesToDelete.map((aliasToDelete) => ({
        id: aliasToDelete.id,
        name: aliasToDelete.name,
        action: 'delete' as const,
        cid: aliasToDelete.cid,
      }));

      const bulkPayload = [
        ...deleteAliasesPayload,
        ...updateAliasesPayload,
        ...createAliasesPayload,
      ];

      const bulkResponse = await bulkGlossaryAliasesUpdate({
        glossaryId: glossary.id,
        payload: bulkPayload,
      });

      const nextState = getNextAliasesState({
        aliases,
        bulkPayload,
        bulkResponse,
        t,
      });

      const updatedAliases = bulkResponse.some((res) => Boolean(res.error));

      if (updatedAliases) {
        setAliases(nextState);
      } else {
        onClose();
      }
    } catch {
      /* empty */
    }
  };

  const canBeCreated = currentAliases.length < codesPerGlossary;
  const isLastAliasInputEmpty = currentAliases.at(-1)?.name?.trim() === '';

  const handleAutoFocus = useCallback(
    (inputElement: HTMLInputElement | null) => {
      if (!inputElement) {
        return;
      }

      const aliasIndex = Number(inputElement.dataset.aliasId);

      if (aliasIndex === autoFocusFieldIndexRef.current) {
        inputElement.scrollIntoView({ behavior: 'smooth' });
        inputElement.focus();
      }
    },
    [],
  );

  return (
    <form onSubmit={handleSubmit}>
      <div className="-m-2 flex max-h-[300px] flex-col gap-2 overflow-auto p-2 pb-5">
        {aliases.map((alias, index) =>
          alias.deleted ? null : (
            <div
              key={alias.cid}
              className={cn(
                'flex gap-2',
                alias.error ? 'items-center' : 'items-end',
              )}
            >
              <Input
                ref={handleAutoFocus}
                data-alias-id={String(index)}
                label={t('Alias {{ aliasNumber }}', {
                  aliasNumber: currentAliases.indexOf(alias) + 1,
                })}
                placeholder={t('Insert alias')}
                value={alias.name}
                onChange={handleChangeAlias(index)}
                helperText={alias.error || ''}
                maxLength={32}
                autoComplete="off"
                size="small"
                color={alias.error ? 'error' : 'default'}
                className="w-full"
              />
              <Button
                type="button"
                onClick={() => handleDeleteAlias(index)}
                aria-label={t('Delete alias {{ aliasNumber }}', {
                  aliasNumber: currentAliases.indexOf(alias) + 1,
                })}
                data-dd-action-name="Delete alias"
                variant="outlined"
                icon
                size="medium"
                disabled={alias.name.trim() === '' && isFormPristine}
              >
                <Delete />
              </Button>
            </div>
          ),
        )}
      </div>
      <div className="mt-6 flex gap-2">
        <Tooltip
          asChild
          className="z-popover"
          description={t('You have reached the maximum number of aliases')}
          side="right"
          disabled={canBeCreated}
        >
          <div>
            <Button
              type="button"
              onClick={handleAddAlias}
              disabled={!canBeCreated || isLastAliasInputEmpty}
              variant="outlined"
              size="small"
              data-dd-action-name="Add alias"
            >
              <Add />
              {t('Add alias')}
            </Button>
          </div>
        </Tooltip>
        <Button
          type="button"
          variant="text"
          size="small"
          className="ml-auto"
          data-dd-action-name="Cancel"
          onClick={onClose}
        >
          {t('Cancel')}
        </Button>
        <Button
          type="submit"
          disabled={isFormPristine || isFormInvalid || isSubmitting}
          size={'small'}
          data-dd-action-name="Confirm"
        >
          {t('Confirm')}
        </Button>
      </div>
    </form>
  );
};

type ExtendedGlossaryAlias = GlossaryAlias & {
  cid: string;
  deleted?: boolean;
  error?: null | string;
};

const getInitialAliasValue = (): ExtendedGlossaryAlias => ({
  id: '',
  cid: globalThis.crypto.randomUUID(),
  name: '',
});
